import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import ExportIcon from '@mui/icons-material/esm/FileDownload';

import { PageHeader, FilterPanel } from '../../components/layout';
import { KPICard, DataTable, Column } from '../../components/common';
import { BarChart } from '../../components/charts';
import budgetService, {
  BudgetLine,
  BudgetSummary,
  PhaseUtilization,
  BudgetOverrun,
  BudgetFilters,
} from '../../services/api/budgetService';
import financialsService, { CostCodeForecastRow } from '../../services/api/financialsService';
import { RootState } from '../../store';
import { budgetProgressWidth, budgetUtilizationPercent, safePercentLabel } from './budgetDisplay';

export default function BudgetPage() {
  const { activeProjectId } = useSelector((state: RootState) => state.projects);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>([]);
  const [costCodeForecast, setCostCodeForecast] = useState<CostCodeForecastRow[]>([]);
  const [phaseUtilization, setPhaseUtilization] = useState<PhaseUtilization[]>([]);
  const [topOverruns, setTopOverruns] = useState<BudgetOverrun[]>([]);
  const [filters, setFilters] = useState<BudgetFilters>({});

  const fetchBudgetData = async () => {
    if (!activeProjectId) {
      setLoading(false);
      setSummary(null);
      setBudgetLines([]);
      setCostCodeForecast([]);
      setPhaseUtilization([]);
      setTopOverruns([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [summaryData, linesData, forecastData, phaseData, overrunsData] = await Promise.all([
        budgetService.getSummary(activeProjectId),
        budgetService.getBudgetLines({ ...filters, projectId: activeProjectId }),
        financialsService.getCostCodeForecast(activeProjectId),
        budgetService.getPhaseUtilization(activeProjectId),
        budgetService.getTopOverruns(activeProjectId, 5),
      ]);

      setSummary(summaryData);
      setBudgetLines(linesData);
      setCostCodeForecast(forecastData);
      setPhaseUtilization(phaseData);
      setTopOverruns(overrunsData);
    } catch (err: any) {
      console.error('Failed to fetch budget data:', err);
      setError(err.response?.data?.message || 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgetData();
  }, [filters, activeProjectId]);

  const handleFilterChange = (field: keyof BudgetFilters, value: string) => {
    setFilters({ ...filters, [field]: value || undefined });
  };

  // CSV is built client-side from the loaded (already filtered) budget lines —
  // there is no backend export endpoint.
  const handleExport = () => {
    const csvField = (value: unknown): string => {
      const text = value === null || value === undefined ? '' : String(value);
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const header = ['WBS', 'Name', 'Category', 'Budget', 'Committed', 'Spent', 'Available', 'Available %', 'Status'];
    const rows = budgetLines.map((line) => [
      line.wbsCode ?? '',
      line.wbsName ?? '',
      line.category,
      line.budget,
      line.committed,
      line.spent,
      line.available,
      line.availablePercent,
      line.status,
    ].map(csvField).join(','));
    const csv = [header.join(','), ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Budget table columns
  const budgetColumns: Column<BudgetLine>[] = [
    {
      id: 'wbsCode',
      label: 'WBS',
      minWidth: 80,
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 120,
    },
    {
      id: 'budget',
      label: 'Budget',
      minWidth: 100,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'committed',
      label: 'Committed',
      minWidth: 100,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'spent',
      label: 'Spent',
      minWidth: 100,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'available',
      label: 'Available',
      minWidth: 120,
      format: (_value: number, row: BudgetLine) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                height: 6,
                bgcolor: 'grey.200',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: budgetProgressWidth(row.spent, row.budget),
                  bgcolor:
                    row.availablePercent < 0
                      ? 'error.main'
                      : row.availablePercent < 20
                      ? 'warning.main'
                      : 'success.main',
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              minWidth: 40,
              color:
                row.availablePercent < 0
                  ? 'error.main'
                  : row.availablePercent < 20
                  ? 'warning.main'
                  : 'success.main',
            }}
          >
            {safePercentLabel(row.availablePercent)}
          </Typography>
        </Box>
      ),
    },
  ];

  // Overrun table columns
  const overrunColumns: Column<BudgetOverrun>[] = [
    {
      id: 'wbsCode',
      label: 'WBS',
      minWidth: 80,
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 120,
    },
    {
      id: 'overrunAmount',
      label: 'Overrun',
      minWidth: 100,
      align: 'right',
      format: (value: number) => (
        <Typography variant="body2" fontWeight="bold" color="error.main">
          -${(Math.abs(value) / 1000).toFixed(0)}K
        </Typography>
      ),
    },
    {
      id: 'overrunPercent',
      label: '%',
      minWidth: 60,
      align: 'right',
      format: (value: number) => (
        <Typography variant="body2" color="error.main">
          {value.toFixed(0)}%
        </Typography>
      ),
    },
  ];

  const forecastColumns: Column<CostCodeForecastRow>[] = [
    { id: 'code', label: 'Cost Code', minWidth: 90 },
    { id: 'name', label: 'Name', minWidth: 160 },
    {
      id: 'budget',
      label: 'Budget',
      minWidth: 100,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'actual',
      label: 'Actual',
      minWidth: 100,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'committed',
      label: 'Committed',
      minWidth: 110,
      align: 'right',
      format: (value: number) => `$${(value / 1000).toFixed(0)}K`,
    },
    {
      id: 'ctc',
      label: 'CTC',
      minWidth: 110,
      align: 'right',
      format: (_value: number, row: CostCodeForecastRow) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, alignItems: 'center' }}>
          <Typography variant="body2">${(row.ctc / 1000).toFixed(0)}K</Typography>
          {row.ctcOverride !== null && row.ctcOverride !== undefined && (
            <Chip size="small" label="Override" color="info" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      id: 'eac',
      label: 'EAC',
      minWidth: 100,
      align: 'right',
      format: (_value: number, row: CostCodeForecastRow) => (
        <Typography variant="body2" color={row.projectedOverrun ? 'error.main' : 'text.primary'}>
          ${(row.eac / 1000).toFixed(0)}K
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Budget Management" />
        <Alert severity="info">Select a project to view budget data.</Alert>
      </Box>
    );
  }

  if (error || !summary) {
    return (
      <Box>
        <PageHeader title="Budget Management" />
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Box>
    );
  }

  const spentPercent = budgetUtilizationPercent(summary.totalSpent, summary.totalBudget);

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Budget Management"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Budget' }]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={budgetLines.length === 0}
            >
              Export CSV
            </Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />}>
              New Budget Line
            </Button>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchBudgetData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* Budget Summary KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Budget"
            value={`$${(summary.totalBudget / 1000000).toFixed(1)}M`}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Committed"
            value="$0.0M"
            changeLabel="deferred"
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Actual Spent"
            value={`$${(summary.totalSpent / 1000000).toFixed(1)}M`}
            change={spentPercent ?? 0}
            changeLabel={spentPercent === null ? 'no budget allocated' : 'of budget'}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Available"
            value={`$${(summary.totalRemaining / 1000000).toFixed(1)}M`}
            change={summary.utilizationPercent - 100}
            changeLabel="remaining"
            color={summary.utilizationStatus === 'OVERRUN' ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Phase</InputLabel>
              <Select
                value={filters.phaseId || ''}
                label="Phase"
                onChange={(e) => handleFilterChange('phaseId', e.target.value)}
              >
                <MenuItem value="">All Phases</MenuItem>
                <MenuItem value="phase-1">Phase 1</MenuItem>
                <MenuItem value="phase-2">Phase 2</MenuItem>
                <MenuItem value="phase-3">Phase 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                label="Category"
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="materials">Materials</MenuItem>
                <MenuItem value="labor">Labor</MenuItem>
                <MenuItem value="equipment">Equipment</MenuItem>
                <MenuItem value="subcontractors">Subcontractors</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="OK">OK</MenuItem>
                <MenuItem value="WARNING">Warning</MenuItem>
                <MenuItem value="OVERRUN">Overrun</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setFilters({})}
              >
                Clear Filters
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </FilterPanel>

      {/* Budget Allocation Table */}
      <Box sx={{ mb: 3 }}>
        <DataTable
          columns={budgetColumns}
          data={budgetLines}
          searchable
          searchPlaceholder="Search budget lines..."
          pagination
          defaultRowsPerPage={25}
          onRowClick={(row) => console.log('View budget line:', row)}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Cost-to-complete by cost code
        </Typography>
        {costCodeForecast.length === 0 ? (
          <Alert severity="info">No cost-code forecast rows are available for this project.</Alert>
        ) : (
          <DataTable
            columns={forecastColumns}
            data={costCodeForecast}
            searchable
            searchPlaceholder="Search cost codes..."
            pagination
            defaultRowsPerPage={10}
          />
        )}
      </Box>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Budget Utilization by Phase */}
        <Grid item xs={12} md={8}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Budget Utilization by Phase
            </Typography>
            <BarChart
              labels={phaseUtilization.map((p) => p.phaseName)}
              datasets={[
                {
                  label: 'Spent',
                  data: phaseUtilization.map((p) => p.totalSpent / 1000000),
                  backgroundColor: '#1976d2',
                },
                {
                  label: 'Budget',
                  data: phaseUtilization.map((p) => p.totalBudget / 1000000),
                  backgroundColor: '#ffa726',
                },
                {
                  label: 'Remaining',
                  data: phaseUtilization.map((p) => p.totalRemaining / 1000000),
                  backgroundColor: '#66bb6a',
                },
              ]}
              height={300}
              horizontal
              stacked
              yAxisLabel="Phase"
              xAxisLabel="Amount ($M)"
            />
          </Box>
        </Grid>

        {/* Top 5 Overruns */}
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Top 5 Overruns
            </Typography>
            {topOverruns.length === 0 ? (
              <Alert severity="success">No budget overruns!</Alert>
            ) : (
              <DataTable
                columns={overrunColumns}
                data={topOverruns}
                pagination={false}
                searchable={false}
                onRowClick={(row) => console.log('View overrun:', row)}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
